import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProviderRegistrationPage.css';
import axios from 'axios';

type ProviderType = 'INDIVIDUAL' | 'CLINIC';
type ProviderRole = 'DOCTOR' | 'DENTIST' | 'NUTRITIONIST' | 'PHYSIOTHERAPIST' | 'AYURVEDIC_PRACTITIONER' | 'HOMEOPATHIC_PRACTITIONER' | 'NURSE' | 'THERAPIST' | 'MEDICINE_REPRESENTATIVE' | 'OTHER';

interface ContactInfo {
  phone: string;
  alternatePhone?: string;
  additionalPhone?: string;
  email?: string;
  website?: string;
}

interface Address {
  street: string;
  city: string;
  state: string;
  pincode: string;
  country?: string;
  landmark?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const ProviderRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [providerType, setProviderType] = useState<ProviderType | null>(null);
  const [showOnlineConsent, setShowOnlineConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Individual provider fields
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<ProviderRole>('DOCTOR');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [qualification, setQualification] = useState('');
  const [experience, setExperience] = useState<number>(0);
  const [bio, setBio] = useState('');
  const [languagesSpoken, setLanguagesSpoken] = useState<string[]>(['English']);
  const [availableForOnline, setAvailableForOnline] = useState(false);
  const [availableForOffline, setAvailableForOffline] = useState(true);
  const [onlineConsentAccepted, setOnlineConsentAccepted] = useState(false);

  // Clinic fields
  const [clinicName, setClinicName] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [establishedYear, setEstablishedYear] = useState<number>(new Date().getFullYear());
  const [staffCount, setStaffCount] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [facilities, setFacilities] = useState<string[]>([]);
  const [operatingHours, setOperatingHours] = useState('');

  // Common fields
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '',
    alternatePhone: '',
    additionalPhone: '',
    email: '',
    website: '',
  });
  const [address, setAddress] = useState<Address>({
    street: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    landmark: '',
  });

  const allSpecialties = [
    'General Physician', 'Cardiology', 'Dermatology', 'Orthopedics', 'Neurology',
    'Pediatrics', 'Gynecology', 'Psychiatry', 'Ophthalmology', 'ENT',
    'Dentistry', 'General Dentistry', 'Orthodontics', 'Periodontics', 'Endodontics', 'Oral Surgery',
    'Nutrition', 'Clinical Nutrition', 'Sports Nutrition', 'Pediatric Nutrition', 'Weight Management',
    'Ayurveda', 'Homeopathy', 'Physiotherapy', 'Oncology', 'Gastroenterology',
    'Nephrology', 'Urology', 'Pulmonology', 'Endocrinology', 'Rheumatology',
  ];

  const handleSpecialtyToggle = (specialty: string) => {
    if (specialties.includes(specialty)) {
      setSpecialties(specialties.filter(s => s !== specialty));
    } else {
      if (specialties.length < (providerType === 'CLINIC' ? 10 : 5)) {
        setSpecialties([...specialties, specialty]);
      }
    }
  };

  const handleLanguageToggle = (lang: string) => {
    if (languagesSpoken.includes(lang)) {
      setLanguagesSpoken(languagesSpoken.filter(l => l !== lang));
    } else {
      setLanguagesSpoken([...languagesSpoken, lang]);
    }
  };

  const handleFacilityToggle = (facility: string) => {
    if (facilities.includes(facility)) {
      setFacilities(facilities.filter(f => f !== facility));
    } else {
      setFacilities([...facilities, facility]);
    }
  };

  const handleOnlineAvailabilityChange = (checked: boolean) => {
    setAvailableForOnline(checked);
    if (checked && !onlineConsentAccepted) {
      setShowOnlineConsent(true);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please login to register as a provider');
      }

      if (providerType === 'INDIVIDUAL') {
        if (!fullName || !contactInfo.phone || !address.street || !address.city || !address.state || !address.pincode || specialties.length === 0) {
          throw new Error('Please fill all required fields');
        }

        if (availableForOnline && !onlineConsentAccepted) {
          throw new Error('Please accept online consultation consent');
        }

        const payload = {
          fullName,
          role,
          specialties,
          contactInfo,
          address,
          registrationNumber,
          qualification,
          experience,
          bio,
          languagesSpoken,
          availableForOnline,
          availableForOffline,
          ...(availableForOnline && {
            onlineConsultationConsent: {
              accepted: true,
              acceptedAt: new Date().toISOString(),
              platforms: ['Zoom', 'Google Meet', 'Amazon Chime'],
              version: '1.0',
            },
          }),
        };

        await axios.post(`${API_URL}/providers/register/individual`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        // Clinic registration
        if (!clinicName || !businessEmail || !contactInfo.phone || !address.street || !address.city || !address.state || !address.pincode || specialties.length === 0) {
          throw new Error('Please fill all required fields');
        }

        const payload = {
          clinicName,
          businessEmail,
          specialties,
          contactInfo,
          address,
          licenseNumber,
          establishedYear,
          staffCount,
          description,
          facilities,
          operatingHours,
        };

        await axios.post(`${API_URL}/providers/register/clinic`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (!providerType) {
    return (
      <div className="provider-registration-container">
        <div className="provider-type-selection">
          <h1>Register as Healthcare Provider</h1>
          <p>Choose your registration type</p>
          
          <div className="provider-type-cards">
            <div className="provider-type-card" onClick={() => setProviderType('INDIVIDUAL')}>
              <div className="card-icon">👨‍⚕️</div>
              <h2>Individual Provider</h2>
              <p>Register as a doctor, dentist, nutritionist, or healthcare professional</p>
              <ul>
                <li>✓ Personal profile</li>
                <li>✓ Online & offline consultations</li>
                <li>✓ Specialty showcase</li>
              </ul>
            </div>

            <div className="provider-type-card" onClick={() => setProviderType('CLINIC')}>
              <div className="card-icon">🏥</div>
              <h2>Clinic / Healthcare Facility</h2>
              <p>Register your clinic or healthcare business</p>
              <ul>
                <li>✓ Business profile</li>
                <li>✓ Multiple specialties</li>
                <li>✓ Offline appointments only</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-registration-container">
      <div className="provider-registration-form">
        <button className="back-button" onClick={() => setProviderType(null)}>
          ← Back to Type Selection
        </button>

        <h1>{providerType === 'INDIVIDUAL' ? 'Individual Provider' : 'Clinic'} Registration</h1>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">Registration successful! Redirecting...</div>}

        <form onSubmit={handleSubmit}>
          {/* Individual Provider Fields */}
          {providerType === 'INDIVIDUAL' && (
            <>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="Dr. John Doe"
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select value={role} onChange={(e) => setRole(e.target.value as ProviderRole)} required>
                  <option value="DOCTOR">Doctor</option>
                  <option value="DENTIST">Dentist</option>
                  <option value="NUTRITIONIST">Nutritionist</option>
                  <option value="PHYSIOTHERAPIST">Physiotherapist</option>
                  <option value="AYURVEDIC_PRACTITIONER">Ayurvedic Practitioner</option>
                  <option value="HOMEOPATHIC_PRACTITIONER">Homeopathic Practitioner</option>
                  <option value="NURSE">Nurse</option>
                  <option value="THERAPIST">Therapist</option>
                  <option value="MEDICINE_REPRESENTATIVE">Medicine Representative (MR)</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Registration Number</label>
                <input
                  type="text"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  placeholder="Medical council registration number"
                />
              </div>

              <div className="form-group">
                <label>Qualification</label>
                <input
                  type="text"
                  value={qualification}
                  onChange={(e) => setQualification(e.target.value)}
                  placeholder="MBBS, MD, BDS, etc."
                />
              </div>

              <div className="form-group">
                <label>Years of Experience</label>
                <input
                  type="number"
                  value={experience}
                  onChange={(e) => setExperience(parseInt(e.target.value))}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about yourself and your expertise"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Languages Spoken</label>
                <div className="checkbox-group">
                  {['English', 'Hindi', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Kannada', 'Malayalam'].map(lang => (
                    <label key={lang} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={languagesSpoken.includes(lang)}
                        onChange={() => handleLanguageToggle(lang)}
                      />
                      {lang}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={availableForOnline}
                    onChange={(e) => handleOnlineAvailabilityChange(e.target.checked)}
                  />
                  Available for Online Consultations
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={availableForOffline}
                    onChange={(e) => setAvailableForOffline(e.target.checked)}
                  />
                  Available for Offline Consultations
                </label>
              </div>
            </>
          )}

          {/* Clinic Fields */}
          {providerType === 'CLINIC' && (
            <>
              <div className="form-group">
                <label>Clinic Name *</label>
                <input
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  required
                  placeholder="City Health Clinic"
                />
              </div>

              <div className="form-group">
                <label>Business Email *</label>
                <input
                  type="email"
                  value={businessEmail}
                  onChange={(e) => setBusinessEmail(e.target.value)}
                  required
                  placeholder="info@clinic.com"
                />
              </div>

              <div className="form-group">
                <label>License Number</label>
                <input
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="Business license number"
                />
              </div>

              <div className="form-group">
                <label>Established Year</label>
                <input
                  type="number"
                  value={establishedYear}
                  onChange={(e) => setEstablishedYear(parseInt(e.target.value))}
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>

              <div className="form-group">
                <label>Staff Count</label>
                <input
                  type="number"
                  value={staffCount}
                  onChange={(e) => setStaffCount(parseInt(e.target.value))}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your clinic and services"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Facilities</label>
                <div className="checkbox-group">
                  {['X-Ray', 'Lab', 'Pharmacy', 'Emergency', 'ICU', 'Operation Theater', 'Dental Chair', 'Consultation Rooms'].map(facility => (
                    <label key={facility} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={facilities.includes(facility)}
                        onChange={() => handleFacilityToggle(facility)}
                      />
                      {facility}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Operating Hours</label>
                <input
                  type="text"
                  value={operatingHours}
                  onChange={(e) => setOperatingHours(e.target.value)}
                  placeholder="Mon-Fri: 9AM-6PM, Sat: 9AM-2PM"
                />
              </div>

              <div className="info-box">
                <strong>Note:</strong> Clinics can only provide offline services and appointments.
              </div>
            </>
          )}

          {/* Common Fields: Specialties */}
          <div className="form-group">
            <label>Specialties * (Max {providerType === 'CLINIC' ? '10' : '5'})</label>
            <div className="checkbox-group specialties-grid">
              {allSpecialties.map(specialty => (
                <label key={specialty} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={specialties.includes(specialty)}
                    onChange={() => handleSpecialtyToggle(specialty)}
                    disabled={!specialties.includes(specialty) && specialties.length >= (providerType === 'CLINIC' ? 10 : 5)}
                  />
                  {specialty}
                </label>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <h3>Contact Information</h3>
          <div className="form-group">
            <label>Primary Phone *</label>
            <input
              type="tel"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
              required
              placeholder="+91-XXXXXXXXXX"
            />
          </div>

          <div className="form-group">
            <label>Alternate Phone</label>
            <input
              type="tel"
              value={contactInfo.alternatePhone}
              onChange={(e) => setContactInfo({ ...contactInfo, alternatePhone: e.target.value })}
              placeholder="+91-XXXXXXXXXX"
            />
          </div>

          {providerType === 'CLINIC' && (
            <div className="form-group">
              <label>Additional Phone</label>
              <input
                type="tel"
                value={contactInfo.additionalPhone}
                onChange={(e) => setContactInfo({ ...contactInfo, additionalPhone: e.target.value })}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={contactInfo.email}
              onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
              placeholder="contact@example.com"
            />
          </div>

          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              value={contactInfo.website}
              onChange={(e) => setContactInfo({ ...contactInfo, website: e.target.value })}
              placeholder="https://example.com"
            />
          </div>

          {/* Address */}
          <h3>Address for {providerType === 'CLINIC' ? 'Clinic' : 'Offline Consultations'}</h3>
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              value={address.street}
              onChange={(e) => setAddress({ ...address, street: e.target.value })}
              required
              placeholder="123 Main Street"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
                required
                placeholder="Bangalore"
              />
            </div>

            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                value={address.state}
                onChange={(e) => setAddress({ ...address, state: e.target.value })}
                required
                placeholder="Karnataka"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pincode *</label>
              <input
                type="text"
                value={address.pincode}
                onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                required
                placeholder="560001"
                pattern="[0-9]{6}"
              />
            </div>

            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={address.country}
                onChange={(e) => setAddress({ ...address, country: e.target.value })}
                placeholder="India"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Landmark</label>
            <input
              type="text"
              value={address.landmark}
              onChange={(e) => setAddress({ ...address, landmark: e.target.value })}
              placeholder="Near City Hospital"
            />
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Registering...' : 'Complete Registration'}
          </button>
        </form>
      </div>

      {/* Online Consultation Consent Modal */}
      {showOnlineConsent && (
        <div className="modal-overlay" onClick={() => setShowOnlineConsent(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Online Consultation Consent</h2>
            <div className="consent-text">
              <p>By accepting this consent, you agree to:</p>
              <ul>
                <li>Provide online healthcare consultations via video conferencing platforms</li>
                <li>Use platforms such as Zoom, Google Meet, Amazon Chime, or other digital video conferencing tools</li>
                <li>Maintain patient confidentiality during online sessions</li>
                <li>Comply with telemedicine guidelines and regulations</li>
                <li>Ensure secure and HIPAA-compliant communication</li>
              </ul>
              <p className="note">
                <strong>Note:</strong> This consent is required only once for providers offering online consultation services.
                You can update your online availability preferences later in your profile settings.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowOnlineConsent(false);
                  setAvailableForOnline(false);
                }}
              >
                Decline
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setOnlineConsentAccepted(true);
                  setShowOnlineConsent(false);
                }}
              >
                Accept Consent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
